// src/lib/stack-app/apps/implementations/session-replay.ts
import { isBrowserLike } from "@stackframe/stack-shared/dist/utils/env";
import { runAsynchronously } from "@stackframe/stack-shared/dist/utils/promises";
import { Result } from "@stackframe/stack-shared/dist/utils/results";
function analyticsOptionsToJson(options) {
  if (!options?.replays?.blockClass) return options;
  const { blockClass, ...rest } = options.replays;
  if (!(blockClass instanceof RegExp)) return options;
  return {
    replays: {
      ...rest,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      blockClass: { __regexp: blockClass.source, __flags: blockClass.flags }
    }
  };
}
function analyticsOptionsFromJson(json) {
  if (!json?.replays?.blockClass) return json;
  const { blockClass, ...rest } = json.replays;
  if (typeof blockClass === "object" && "__regexp" in blockClass) {
    const bc = blockClass;
    return {
      replays: {
        ...rest,
        blockClass: new RegExp(bc.__regexp, bc.__flags)
      }
    };
  }
  return json;
}
var LOCAL_STORAGE_PREFIX = "stack:session-replay:v1";
var IDLE_TTL_MS = 3 * 60 * 1e3;
var FLUSH_INTERVAL_MS = 5e3;
var MAX_EVENTS_PER_BATCH = 200;
var MAX_APPROX_BYTES_PER_BATCH = 512e3;
function safeParseStoredSession(raw) {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) return null;
    if (typeof parsed.session_id !== "string") return null;
    if (typeof parsed.created_at_ms !== "number") return null;
    if (typeof parsed.last_activity_ms !== "number") return null;
    return parsed;
  } catch {
    return null;
  }
}
function makeStorageKey(projectId) {
  return `${LOCAL_STORAGE_PREFIX}:${projectId}`;
}
function generateUuid() {
  return crypto.randomUUID();
}
function getOrRotateSession(options) {
  const existing = safeParseStoredSession(localStorage.getItem(options.key));
  if (existing && options.nowMs - existing.last_activity_ms <= IDLE_TTL_MS) {
    return existing;
  }
  const next = {
    session_id: generateUuid(),
    created_at_ms: options.nowMs,
    last_activity_ms: options.nowMs
  };
  localStorage.setItem(options.key, JSON.stringify(next));
  return next;
}
var SessionRecorder = class {
  constructor(deps, replayOptions) {
    this._started = false;
    this._cancelled = false;
    this._stopRecording = null;
    this._detachListeners = null;
    this._flushTimer = null;
    this._events = [];
    this._approxBytes = 0;
    this._lastPersistActivity = 0;
    this._recording = false;
    this._rrwebModule = null;
    this._lastBrowserSessionId = null;
    this._takingSnapshot = false;
    this._flushInProgress = false;
    this._lastKnownAccessToken = null;
    this._deps = deps;
    this._replayOptions = replayOptions;
    this._sessionReplaySegmentId = generateUuid();
    this._storageKey = makeStorageKey(deps.projectId);
  }
  /**
   * Starts recording. Idempotent — calling multiple times is safe.
   */
  start() {
    if (this._started) return;
    if (!isBrowserLike()) return;
    this._started = true;
    runAsynchronously(() => this._startRecording(), { noErrorLogging: true });
    this._flushTimer = setInterval(() => this._tick(), FLUSH_INTERVAL_MS);
  }
  stop() {
    this._cancelled = true;
    if (this._flushTimer !== null) {
      clearInterval(this._flushTimer);
      this._flushTimer = null;
    }
    runAsynchronously(() => this._flush({ keepalive: true }), { noErrorLogging: true });
    this._stopCurrentRecording();
  }
  clearBuffer() {
    this._events = [];
    this._approxBytes = 0;
  }
  _persistActivity(nowMs) {
    const stored = getOrRotateSession({ key: this._storageKey, nowMs });
    if (nowMs - this._lastPersistActivity < 5e3) return stored;
    this._lastPersistActivity = nowMs;
    const updated = { ...stored, last_activity_ms: nowMs };
    localStorage.setItem(this._storageKey, JSON.stringify(updated));
    return stored;
  }
  async _flush(options) {
    if (!this._lastKnownAccessToken) return;
    if (this._events.length === 0) return;
    if (this._flushInProgress) return;
    const nowMs = Date.now();
    const stored = getOrRotateSession({ key: this._storageKey, nowMs });
    const batchId = generateUuid();
    const payload = {
      browser_session_id: stored.session_id,
      session_replay_segment_id: this._sessionReplaySegmentId,
      batch_id: batchId,
      started_at_ms: stored.created_at_ms,
      sent_at_ms: nowMs,
      events: this._events
    };
    this._events = [];
    this._approxBytes = 0;
    this._flushInProgress = true;
    try {
      const res = await this._deps.sendBatch(
        JSON.stringify(payload),
        { keepalive: options.keepalive }
      );
      if (res.status === "error") {
        console.warn("SessionRecorder flush failed:", res.error);
        return;
      }
      if (!res.data.ok) {
        console.warn("SessionRecorder flush failed:", res.data.status, await res.data.text());
      }
    } finally {
      this._flushInProgress = false;
    }
  }
  async _startRecording() {
    if (this._recording || this._cancelled) return;
    if (!this._rrwebModule) {
      const rrwebImport = await Result.fromPromise(import("rrweb"));
      if (rrwebImport.status === "error") {
        console.warn("SessionRecorder: rrweb import failed. Is rrweb installed?", rrwebImport.error);
        return;
      }
      this._rrwebModule = rrwebImport.data;
    }
    if (this._cancelled) return;
    this._stopRecording = this._rrwebModule.record({
      emit: (event) => {
        const nowMs = Date.now();
        const stored = this._persistActivity(nowMs);
        if (this._lastBrowserSessionId === null) {
          this._lastBrowserSessionId = stored.session_id;
        } else if (stored.session_id !== this._lastBrowserSessionId && !this._takingSnapshot) {
          this._lastBrowserSessionId = stored.session_id;
          this._takingSnapshot = true;
          try {
            this._rrwebModule.record.takeFullSnapshot();
          } finally {
            this._takingSnapshot = false;
          }
        }
        this._events.push(event);
        this._approxBytes += JSON.stringify(event).length;
        if (this._events.length >= MAX_EVENTS_PER_BATCH || this._approxBytes >= MAX_APPROX_BYTES_PER_BATCH) {
          runAsynchronously(() => this._flush({ keepalive: false }), { noErrorLogging: true });
        }
      },
      maskAllInputs: this._replayOptions.maskAllInputs ?? true,
      ...this._replayOptions.blockClass !== void 0 ? { blockClass: this._replayOptions.blockClass } : {},
      ...this._replayOptions.blockSelector !== void 0 ? { blockSelector: this._replayOptions.blockSelector } : {}
    }) ?? null;
    this._recording = true;
    const onPageHide = () => {
      runAsynchronously(() => this._flush({ keepalive: true }), { noErrorLogging: true });
    };
    window.addEventListener("pagehide", onPageHide);
    document.addEventListener("visibilitychange", onPageHide);
    this._detachListeners = () => {
      window.removeEventListener("pagehide", onPageHide);
      document.removeEventListener("visibilitychange", onPageHide);
    };
  }
  _stopCurrentRecording() {
    if (this._detachListeners) {
      this._detachListeners();
      this._detachListeners = null;
    }
    if (this._stopRecording) {
      this._stopRecording();
      this._stopRecording = null;
    }
    this._events = [];
    this._approxBytes = 0;
    this._recording = false;
  }
  _tick() {
    if (this._cancelled) return;
    runAsynchronously(async () => {
      this._lastKnownAccessToken = await this._deps.getAccessToken();
    }, { noErrorLogging: true });
    const hasAuth = !!this._lastKnownAccessToken;
    if (hasAuth && this._events.length > 0) {
      runAsynchronously(() => this._flush({ keepalive: false }), { noErrorLogging: true });
    }
  }
};
export {
  SessionRecorder,
  analyticsOptionsFromJson,
  analyticsOptionsToJson,
  generateUuid,
  getOrRotateSession,
  makeStorageKey,
  safeParseStoredSession
};
//# sourceMappingURL=session-replay.js.map